import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Input } from 'components/FormFields';
import Loading from 'components/Loading';
import { POSModal, POSButton } from 'components/POS';
import { usePrintManager } from 'hooks/usePrintManager';
import { useCart } from 'shared/CartContext';
import { validateIpAddress } from 'shared/utils/ValidateIp';

const Container = styled.div`
  padding: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
`;

const LoadingText = styled.p`
  font-size: 16px;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#333'};
  text-align: center;
`;

const STATUS_CONNECTED = 'Connected';

export const ReaderIpModal = ({ isVisible, setIsVisible }) => {
  const [ipAddress, setIpAddress] = useState('');
  const [isValidIp, setIsValidIp] = useState(true);

  const { setConnectionStatus } = useCart();

  const IPAddress = localStorage.getItem('readerIp');

  const { connect, isConnected } = usePrintManager();

  const [showIPInput, setShowIPInput] = useState(false);

  const connectPrinter = async (IPAddressFromInput) => {
    try {
      const targetIp = IPAddressFromInput || IPAddress;
      setConnectionStatus('Connecting ...');

      if (isConnected()) {
        setIsVisible(false);
        setConnectionStatus(STATUS_CONNECTED);
        return true;
      }

      const result = await connect(targetIp, setConnectionStatus, setIsVisible);

      if (result) {
        setConnectionStatus(STATUS_CONNECTED);
        setIsVisible(false);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      alert('Error connecting to printer: ' + error.message);
      return false;
    } finally {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    async function checkPrinterConnection() {
      if (!IPAddress) setShowIPInput(true);
      else if (!isConnected()) {
        try {
          await connectPrinter();
        } catch (error) {
          console.error('Failed to connect to printer:', error);
        }
      } else {
        setIsVisible(false);
      }
    }

    if (isVisible) checkPrinterConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleChange = (e) => {
    const value = e.target.value;
    setIpAddress(value);

    // Validate IP address
    setIsValidIp(validateIpAddress(value));
  };

  const handleContinue = async () => {
    // Save the input IP address to local storage if valid
    if (isValidIp) {
      setShowIPInput(false);
      localStorage.setItem('readerIp', ipAddress);
      try {
        await connectPrinter(ipAddress);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <POSModal
      isVisible={isVisible}
      onClose={handleClose}
      title={
        showIPInput ? 'Input your printer IP Address' : 'Connecting to Printer'
      }
      maxWidth={500}
    >
      <Container>
        {showIPInput ? (
          <>
            <Input
              name="ipAddress"
              label="IP Address"
              placeholder="xxx.xxx.xxx.xxx"
              required
              onChange={handleChange}
              value={ipAddress}
              error={!isValidIp && ipAddress !== ''}
              helperText={
                !isValidIp && ipAddress !== '' ? 'Invalid IP address' : ''
              }
            />

            <ButtonRow>
              <POSButton
                variant="primary"
                onClick={handleContinue}
                disabled={!ipAddress || !isValidIp}
                style={{ minWidth: 120 }}
              >
                Save
              </POSButton>
            </ButtonRow>
          </>
        ) : (
          <LoadingContainer>
            <Loading size={30} />
            <LoadingText>Connection in progress</LoadingText>
          </LoadingContainer>
        )}
      </Container>
    </POSModal>
  );
};
